import { z } from 'zod';

/**
 * A text parameter as the router hands it over. TanStack's default search parser reads every value
 * as JSON first, so a hand-typed `?search=2000` arrives as the NUMBER 2000; it is text here.
 */
const text = z.preprocess(
  (value) => (typeof value === 'number' || typeof value === 'boolean' ? String(value) : value),
  z.string(),
);

/**
 * A list's state lives in the URL (SDD-017 §9, L-12): `?filters=…&search=…&order=…&validAt=…` —
 * shareable, bookmarkable and back-button safe. Every list route spreads this schema into its
 * `validateSearch`:
 *
 *     const policyListSearch = listSearchSchema.extend({ tarif: z.string().optional() });
 *     createRoute({ path: '/policies', validateSearch: policyListSearch, … });
 *
 * and turns what the route hands back into the list request with {@link listParamsOf}; a change
 * from the filter or the grid goes back through {@link nextListSearch}, which drops emptied keys
 * so a cleared filter leaves no `?filters=` behind.
 *
 * What the schema does NOT do, deliberately:
 *  - it does not parse `filters` or `order`. An unreadable filter in a link is still sent, and the
 *    SERVICE answers `INVALID_FILTER` with the field and reason, which the grid shows as its error
 *    state ("never all rows"). Validating here would turn a bad link into a silently empty filter.
 *  - `validAt` is the one exception: it feeds the "Stand am" date picker, which cannot show text
 *    that is not a date. A malformed `validAt` therefore falls back to "today" (absent) instead of
 *    failing the route.
 *
 * `asOf` is never in the URL of a LIST: the first page pins it and refresh() starts a new one
 * (§6). The policy DETAIL route carries its own `asOf` (an instant) for a shared audit link.
 */
export const listSearchSchema = z.object({
  /** The `filters` grammar (§5.2), verbatim. */
  filters: text.optional(),
  /** The `search` language (§8.4), verbatim. */
  search: text.optional(),
  /** Comma-separated catalog keys, `-` = descending. */
  order: text.optional(),
  /** Temporal lists only: the business date (`YYYY-MM-DD`); absent = today. */
  validAt: z.iso.date().optional().catch(undefined),
  /**
   * "Covered at any time in a period" (SDD-021 PC-20, the *Zeitraum* of a validity-period
   * selector): `coveredFrom` inclusive, `coveredTo` EXCLUSIVE, `YYYY-MM-DD`. A list sends them
   * instead of `validAt`. Malformed values fall back to absent, like `validAt`.
   */
  coveredFrom: z.iso.date().optional().catch(undefined),
  coveredTo: z.iso.date().optional().catch(undefined),
  /**
   * Rows per page (1–200, §5.1); absent = the service's 50. A link may ask for smaller pages; one
   * the service would clamp or refuse is dropped here rather than failing the route.
   */
  pageSize: z.coerce.number().int().min(1).max(200).optional().catch(undefined),
});

export type ListSearch = z.infer<typeof listSearchSchema>;

/** The list request's view of the URL: only the keys that say something. */
export interface ListParams {
  readonly filters?: string;
  readonly search?: string;
  readonly order?: string;
  readonly validAt?: string;
  readonly coveredFrom?: string;
  readonly coveredTo?: string;
  readonly pageSize?: number;
}

const KEYS = ['filters', 'search', 'order', 'validAt', 'coveredFrom', 'coveredTo'] as const;

/** The URL state as list parameters, empty strings dropped. */
export function listParamsOf(search: Partial<ListSearch>): ListParams {
  const out: Record<string, string> = {};
  for (const key of KEYS) {
    const value = search[key];
    if (typeof value === 'string' && value.trim() !== '') out[key] = value;
  }
  return search.pageSize === undefined ? out : { ...out, pageSize: search.pageSize };
}

/**
 * The next URL search after a change: `patch` merged over `previous`, every list key that became
 * empty removed, and every key the patch empties removed; other keys (a tab, a tarif) are kept.
 */
export function nextListSearch<S extends Partial<ListSearch>>(
  previous: S,
  patch: Partial<ListSearch> & Readonly<Record<string, unknown>>,
): S {
  const merged: Record<string, unknown> = { ...previous, ...patch };
  const isEmpty = (value: unknown): boolean =>
    value === undefined || (typeof value === 'string' && value.trim() === '');
  const emptied = new Set<string>([
    ...KEYS.filter((key) => isEmpty(merged[key])),
    ...Object.keys(patch).filter((key) => isEmpty(patch[key])),
  ]);
  return Object.fromEntries(Object.entries(merged).filter(([key]) => !emptied.has(key))) as S;
}
